import React, { useRef, useEffect, useState, useContext, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { request } from 'graphql-request'
import useSWR from 'swr'
import { AppContext } from '@state/context'
import {
  setEditMode,
  setCurrentSection,
  setSelectedSectionId,
  saveSectionSuccess,
  saveSection,
  saveSectionFailed,
} from '@state/actions/appActions'
import { openPanel } from '@state/actions/panelsActions'
import { Single_Section } from '@graphql/queries'
import { Update_Section } from '@graphql/mutations'
import SideBarPage from '@views/SideBarPage'
import Editor from '@components/Editor'
import Button from '@components/Button'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  COLORS,
  HEADING_TYPE,
  PANELS,
  TEXT_SIZES,
  DIVIDER_SIZE,
  USER_ROLES,
  CONTAINER_STYLE,
  EXAM_DISPLAY_TYPES,
} from '@helpers/constants'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Loader from '@components/Loader'
import Text from '@components/Text'
import SideBar from '@components/SideBar'
import ContentHeader from '@components/ContentHeader'
import SubjectHeader from '@components/SubjectHeader'
import SectionRenderer from '@components/SectionRenderer'
import { useUpdateSection } from '@hooks/mutations/useUpdateSection'
import ButtonWithLoader from '@components/ButtonWithLoader'
import { clearOnSaveCallbacks } from '@state/actions/editorActions'
import Exams from '@components/Exams'
import QuizExamView from '@components/Exams/QuizExamView'
import Container from '@components/Container'
import SectionEnderFeedback from '@components/SectionEnderFeedback'
import { useOwnBooksAllowed } from '@hooks/useOwnBooksAllowed'

const SingleSection = () => {
  const { id } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const [state, dispatch] = useContext(AppContext)
  const onSaveCallbackRef = useRef()
  const ownBooksAllowed = useOwnBooksAllowed()
  const {
    app: { editMode, currentSection, userId, userRole, currentActiveExam },
    editor: { onSaveCallbacks },
  } = state
  const isSaved = currentSection?.customers?.some(
    (customer) => customer?.id === userId
  )
  const { post, isPosting } = useUpdateSection()

  const addToBookcase = async (connectCustomers = []) => {
    await post(
      {
        id: id,
        connectCustomers: connectCustomers,
        disconnectCustomers: [],
      },
      'Sparad i bokhyllan.',
      'Nåt gick fel när avsnittet skulle sparas i bokhyllan.'
    )
  }

  const { data, error } = useSWR([Single_Section, id])

  useEffect(() => {
    onSaveCallbackRef.current = onSaveCallbacks
  }, [onSaveCallbacks])

  useEffect(() => {
    if (data && !error) {
      const section = data?.section

      if (!section) {
        navigate('/401', { replace: true })
        return
      }

      dispatch(setCurrentSection(section))
    }
    return () => {
      dispatch(setCurrentSection(null))
    }
  }, [data, error])

  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    dispatch(setEditMode(false))
    setHasMounted(true)

    return () => {
      dispatch(setCurrentSection(null))
      dispatch(clearOnSaveCallbacks())
    }
  }, [])

  const editorRef = useRef()

  const save = async () => {
    setIsLoading(true)

    await Promise.all(
      Object.values(onSaveCallbackRef.current).map(
        async (callback) => await callback()
      )
    )

    dispatch(saveSection())
    try {
      const currentContent = await editorRef.current.getContent()

      if (currentContent) {
        const currentContentStr = JSON.stringify(currentContent)

        const variables = {
          id: currentSection.id,
          content: currentContentStr,
          teachersGuideContent: currentSection.teachersGuideContent,
        }

        const res = await request('/graphql', Update_Section, variables)

        if (res?.updateSection?.id) {
          // Section update success
          console.log('🚀 ~ Section update success, id:', res.updateSection.id)
          dispatch(saveSectionSuccess())
        } else {
          console.log('🚀 ~ could not update')
          dispatch(saveSectionFailed())
        }
      } else {
        console.log('🚀 ~ editor not ready for save')
        dispatch(saveSectionFailed())
      }
    } catch (error) {
      console.log('🚀 ~ error saving', error)
      dispatch(saveSectionFailed())
    }
    setIsLoading(false)
  }

  const switchToPreviewMode = async () => {
    dispatch(setEditMode(false))
    const currentContent = await editorRef.current.getContent()
    const updatedSection = {
      ...currentSection,
      content: currentContent,
    }

    dispatch(setCurrentSection(updatedSection))
    setIsLoading(false)
  }
  const breadcrumb = useMemo(
    () =>
      currentSection?.id === id && {
        label: currentSection?.name,
      },
    [currentSection]
  )

  if (
    currentActiveExam &&
    currentActiveExam.displayType ===
      parseInt(EXAM_DISPLAY_TYPES.FULL_SCREEN.value)
  ) {
    return (
      <Container size={CONTAINER_STYLE.DESKTOP}>
        {/* Quiz Exam in Fullscreen */}
        <QuizExamView fullScreen />
      </Container>
    )
  }

  return (
    <SideBarPage>
      <SideBar breadcrumb={breadcrumb}>
        <Divider size={DIVIDER_SIZE.SMALL} />

        {currentSection && (
          <>
            {userRole === USER_ROLES.ADMIN &&
              currentSection?.tags &&
              currentSection?.tags.length > 0 && (
                <>
                  {currentSection?.tags?.map((tag) => (
                    <Text
                      key={tag.id}
                      color={COLORS.WHITE.TEXT}
                      extraClasses={'mr-2 leading-relaxed'}
                    >
                      #{tag.name}
                    </Text>
                  ))}
                  <Divider size={DIVIDER_SIZE.SMALL} />
                </>
              )}

            <Button
              type={BUTTON_TYPE.MEDIUM_DARK_GREY}
              icon={'Information'}
              iconLeft
              size={BUTTON_SIZE.MEDIUM}
              handleClick={() => {
                dispatch(setSelectedSectionId(id))
                dispatch(openPanel(PANELS.VIEW_SECTION))
              }}
            >
              Information om avsnittet
            </Button>

            <Divider size={DIVIDER_SIZE.SMALL} />

            <Divider
              size={DIVIDER_SIZE.LINE}
              color={COLORS.LIGHT_DARK_GREY.BORDER}
              line
            />

            <Divider size={DIVIDER_SIZE.SMALL} />

            {ownBooksAllowed && userId !== currentSection?.creator?.id && (
              <div className={'flex flex-col items-start mb-4'}>
                <div className={'mb-4'}>
                  <Button
                    type={BUTTON_TYPE.MEDIUM_DARK_GREY}
                    size={BUTTON_SIZE.LARGE}
                    disabled={isPosting || isSaved}
                    handleClick={() => addToBookcase([userId], [])}
                    icon={'Favourite'}
                    iconRight
                  >
                    {isSaved ? 'Sparat i min bokhylla' : 'Spara i min bokhylla'}
                  </Button>
                </div>

                <Button
                  type={BUTTON_TYPE.MEDIUM_DARK_GREY}
                  size={BUTTON_SIZE.LARGE}
                  icon={'Book'}
                  iconRight
                  handleClick={() => {
                    dispatch(setSelectedSectionId(id))
                    dispatch(setCurrentSection(currentSection))
                    dispatch(openPanel(PANELS.ADD_SECTION_TO_BOOK))
                  }}
                >
                  Lägg till i bok
                </Button>
              </div>
            )}
          </>
        )}

        {userId === currentSection?.creator?.id && !editMode && (
          <div className={'mb-4'}>
            <Button
              handleClick={() => {
                dispatch(setEditMode(true))
              }}
              textColor={COLORS.WHITE.TEXT}
              backgroundColor={COLORS.PRIMARY_GREEN.BACKGROUND}
              type={BUTTON_TYPE.PRIMARY}
              size={BUTTON_SIZE.LARGE}
              icon={'Pencil'}
              iconLeft
            >
              Redigera
            </Button>
          </div>
        )}

        {editMode && (
          <div className={'flex items-center mb-4'}>
            <div className={'mr-4'}>
              <ButtonWithLoader
                handleClick={save}
                enabledText={'Spara'}
                isLoading={isLoading}
                type={BUTTON_TYPE.PRIMARY}
                size={BUTTON_SIZE.LARGE}
                icon={'ClipboardAdd'}
                iconLeft
              />
            </div>
            <div className={'mr-4'}>
              <Button
                handleClick={switchToPreviewMode}
                textColor={COLORS.WHITE.TEXT}
                backgroundColor={COLORS.PRIMARY_GREEN.BACKGROUND}
                type={BUTTON_TYPE.PRIMARY}
                size={BUTTON_SIZE.LARGE}
                iconLeft
                icon={'Eye'}
              >
                Förhandsgranska
              </Button>
            </div>
          </div>
        )}
      </SideBar>

      {/* main area */}
      <SideBarPage.Content>
        {!data && !error && <Loader />}
        {error && <p>Error loading section</p>}

        {data && !error && hasMounted && currentSection && (
          <>
            <ContentHeader hideButtons={userRole === USER_ROLES.STUDENT} />

            {editMode && (
              <>
                <div
                  className={
                    'flex flex-col items-start my-0 mx-auto max-w-[700px]'
                  }
                >
                  <Button
                    size={BUTTON_SIZE.NO_PADDING}
                    handleClick={() => {
                      dispatch(openPanel(PANELS.EDIT_SECTION))
                    }}
                    icon={'Pencil'}
                    iconRight
                  >
                    <SubjectHeader subject={currentSection?.subject} dark />
                  </Button>

                  <Button
                    icon={'Pencil'}
                    iconRight
                    size={'noPadding'}
                    handleClick={() => {
                      dispatch(openPanel(PANELS.EDIT_SECTION))
                    }}
                  >
                    <Heading
                      headingLevel={HEADING_TYPE.H1}
                      size={TEXT_SIZES.XXXLARGE}
                    >
                      {currentSection.name}
                    </Heading>
                  </Button>
                </div>

                <div className='max-w-[700px] mx-auto'>
                  <Editor
                    ref={editorRef}
                    content={
                      typeof currentSection?.content === 'string'
                        ? JSON.parse(currentSection.content)
                        : currentSection.content
                    }
                    onSave={save}
                    elementId={'editorjs'}
                  />
                </div>
              </>
            )}

            {!editMode && <SectionRenderer section={currentSection} />}

            {/* Exams */}
            <Exams section={currentSection} editMode={editMode} />

            {/* Feedback */}
            {!editMode && <SectionEnderFeedback section={currentSection} />}
          </>
        )}
      </SideBarPage.Content>
    </SideBarPage>
  )
}

export default SingleSection
